import type { ResourceDef, ResourceId } from '../types';

/**
 * Every material and currency in the game. `age` controls when a resource
 * becomes visible; `nutrition` and `foodPriority` control what the tribe eats
 * and in what order (lowest priority number is eaten first).
 */
export const RESOURCES: ResourceDef[] = [
  // --- Age I: Scavenging ---------------------------------------------------
  {
    id: 'forage',
    name: 'Forage',
    plural: 'Forage',
    glyph: 'leaf',
    age: 0,
    baseCap: 200,
    nutrition: 1,
    foodPriority: 3,
    description: 'Roots, berries, shoots and grubs. Thin food, but it is food.',
  },
  {
    id: 'flint',
    name: 'Flint',
    plural: 'Flint',
    glyph: 'stone',
    age: 0,
    baseCap: 120,
    description: 'Stone that breaks to an edge. The first thing the tribe made on purpose.',
  },
  {
    id: 'tinder',
    name: 'Tinder',
    plural: 'Tinder',
    glyph: 'grass',
    age: 0,
    baseCap: 120,
    description: 'Dry grass and shredded bark, gathered against a spark that has not come yet.',
  },
  {
    id: 'insight',
    name: 'Insight',
    plural: 'Insight',
    glyph: 'eye',
    age: 0,
    baseCap: 100000,
    isCurrency: true,
    description: 'What the tribe has worked out and not yet forgotten. Spent on discoveries.',
  },

  // --- Age II: Fire --------------------------------------------------------
  {
    id: 'ember',
    name: 'Embers',
    plural: 'Embers',
    glyph: 'flame',
    age: 1,
    baseCap: 150,
    description: 'The living coal. Carried, fed, and never allowed to go out.',
  },
  {
    id: 'charcoal',
    name: 'Charcoal',
    plural: 'Charcoal',
    glyph: 'coal',
    age: 1,
    baseCap: 150,
    description: 'Wood burned slow and starved of air. Burns hotter than what made it.',
  },

  // --- Age III: The Hunt ---------------------------------------------------
  {
    id: 'meat',
    name: 'Meat',
    plural: 'Meat',
    glyph: 'deer',
    age: 2,
    baseCap: 180,
    nutrition: 3,
    foodPriority: 2,
    description: 'Heavy food. Three days of foraging in a single carry.',
  },
  {
    id: 'hide',
    name: 'Hides',
    plural: 'Hides',
    glyph: 'hide',
    age: 2,
    baseCap: 100,
    description: 'Raw skin, stiff and rotting. Worth nothing until worked.',
  },
  {
    id: 'bone',
    name: 'Bone',
    plural: 'Bone',
    glyph: 'bone',
    age: 2,
    baseCap: 100,
    description: 'Awls, needles, points and beads. The parts of the kill that outlast the meal.',
  },
  {
    id: 'leather',
    name: 'Leather',
    plural: 'Leather',
    glyph: 'leather',
    age: 2,
    baseCap: 100,
    description: 'Hide scraped, smoked and softened. Clothing, cordage, cover.',
  },

  // --- Age IV: Shelter -----------------------------------------------------
  {
    id: 'timber',
    name: 'Timber',
    plural: 'Timber',
    glyph: 'tree',
    age: 3,
    baseCap: 250,
    description: 'Cut poles and split beams. Nothing stands up without it.',
  },
  {
    id: 'fiber',
    name: 'Fibre',
    plural: 'Fibre',
    glyph: 'thread',
    age: 3,
    baseCap: 200,
    description: 'Nettle, bast and sinew, twisted until it holds against a pull.',
  },

  // --- Age V: Lore ---------------------------------------------------------
  {
    id: 'ochre',
    name: 'Ochre',
    plural: 'Ochre',
    glyph: 'hand',
    age: 4,
    baseCap: 160,
    description: 'Red earth ground fine. The colour the tribe uses to mean something.',
  },

  // --- Age VI: Seeds -------------------------------------------------------
  {
    id: 'grain',
    name: 'Grain',
    plural: 'Grain',
    glyph: 'grain',
    age: 5,
    baseCap: 400,
    nutrition: 2,
    foodPriority: 1,
    description: 'Food that comes back to the same place, at the same time, every year.',
  },
  {
    id: 'clay',
    name: 'Clay',
    plural: 'Clay',
    glyph: 'clay',
    age: 5,
    baseCap: 250,
    description: 'Riverbank mud that holds a shape and then holds it forever.',
  },
  {
    id: 'pottery',
    name: 'Pottery',
    plural: 'Pottery',
    glyph: 'pot',
    age: 5,
    baseCap: 200,
    description: 'Fired vessels. Grain kept in one is grain the winter cannot reach.',
  },
];

export const RESOURCES_BY_ID: Record<ResourceId, ResourceDef> = Object.fromEntries(
  RESOURCES.map((resource) => [resource.id, resource]),
);

export const RESOURCE_IDS: ResourceId[] = RESOURCES.map((resource) => resource.id);

/** Resources the tribe can eat, ordered by the priority they are eaten in. */
export const FOOD_RESOURCES: ResourceDef[] = RESOURCES.filter(
  (resource) => resource.nutrition !== undefined,
).sort((a, b) => (a.foodPriority ?? 99) - (b.foodPriority ?? 99));

export function getResource(id: ResourceId): ResourceDef | undefined {
  return RESOURCES_BY_ID[id];
}
